import { useCallback, useEffect, useMemo, useState } from "react";
import { getIn, useField, useFormikContext } from "formik";
import { array, mixed, object, string } from "yup";
import { Banner, Button, Flex, OverlayStyle, SlideoutDrawerV2, Text } from "@jsq/ds/core";
import { Form, SubmitButton } from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { ComponentKind } from "~/graphql/supergraphEnums";
import { SPACING } from "~/styles/variables";
import { DataFormatSection } from "../../../../shared/DataFormatSection";
import { DatePeriodSection } from "../../../../shared/DatePeriodSection";
import { DEFAULT_DATE_PERIOD_VALUES } from "../../../../shared/DatePeriodSection/constants";
import { datePeriodSchema } from "../../../../shared/DatePeriodSection/validation";
import {
  getDefaultRollupLevelValue,
  normalizeLevelStack,
} from "../../../../shared/levels/levelDisplay";
import { NONE_LEVEL_VALUE } from "../../../../shared/levels/noneLevel";
import { filterLevelOptionsBySupported } from "../../../../shared/levels/supportedLevels";
import { RulesSection } from "../../../../shared/RulesSection";
import { rulesSchema } from "../../../../shared/RulesSection/validation";
import { getNameDisplayText, toTemplateTag } from "../fieldsMapper";
import { DEFAULT_FIELD_SETTINGS_VALUES } from "./constants";
import {
  isNumericFieldType,
  shouldShowLevels,
  toFieldSettingsValues,
  toNodeFieldType,
  toPrimaryFieldSupportedLevels,
} from "./fieldSettingsMapper";
import { FormulaComponentAccordionCard } from "./FormulaComponentAccordionCard";
import { GeneralCard } from "./GeneralCard";
import { LevelsCard } from "./LevelsCard";
import { NodeComponentFieldTypeRadio } from "./NodeComponentFieldTypeRadio";
import * as styles from "./styles_vanilla.css";
import { useFormulaComponentItems } from "./useFormulaComponentItems";
import { Z_INDEX } from "./zIndex";
import type { FormulaComponentAccordionItem } from "./formulaComponentItems";
import type { FieldSettingsValues, FormulaComponentSettings } from "./types";
import type { DataFormatValues } from "../../../../shared/DataFormatSection/types";
import type { QueryRollupLevelOption } from "../../../../shared/levels/types";
import type { AttributeLeaf } from "../../../../shared/RulesSection/attributeCatalogue";
import type { Formula } from "../../FormulasGrid";
import type { TypeHierarchy } from "../../HierarchyTreeGrid";
import type { QueryFieldRow } from "../fieldsMapper";
import type { FieldDataType, QueryFieldKind } from "~/graphql/supergraphTypes";

type FieldSettingsPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  fieldRow: QueryFieldRow | null;
  onSave: (fieldId: string, values: FieldSettingsValues) => Promise<void>;
  /** Query-level format values shown as disabled Data format controls when
   * the field has not overridden the query defaults. */
  queryFormatValues?: DataFormatValues;
  isReadOnly?: boolean;
  arenaId?: number | null;
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>;
  attributeLeaves?: ReadonlyArray<AttributeLeaf>;
  typeHierarchy?: TypeHierarchy | null;
  formulas?: ReadonlyArray<Formula>;
  /** Per-component settings the takeover buffered from a previous panel
   * save of this field (session state, not field state) — merged back
   * into the form so buffered edits survive panel close/reopen. */
  pendingFormulaComponents?: FieldSettingsValues["formulaComponents"];
};

const formulaComponentSettingsSchema = object({
  datePeriod: datePeriodSchema,
  levels: array().of(string()).min(1, "Select a level"),
  filterRules: rulesSchema,
  suppressionRules: rulesSchema,
});

const buildValidationSchema = (
  raw: QueryFieldRow["raw"] | null,
  componentPathKeys: ReadonlyArray<string>,
  supportedLevels: ReadonlyArray<string>,
) =>
  object({
    filterRules: rulesSchema,
    suppressionRules: rulesSchema,
    datePeriod: mixed().when("fieldType", {
      is: (fieldType: FieldDataType) =>
        isNumericFieldType(fieldType) && raw?.referenceKind !== "FORMULA",
      then: () => datePeriodSchema.nullable(),
      otherwise: () => mixed().nullable().notRequired(),
    }),
    levels: array()
      .of(string())
      .when("fieldType", {
        is: (fieldType: FieldDataType) =>
          raw ? shouldShowLevels(raw.referenceKind, fieldType, supportedLevels) : true,
        then: (schema) => schema.min(1, "Select a level"),
        otherwise: (schema) => schema.notRequired(),
      }),
    // Shape keyed by the expansion's resolved pathKeys so errors land on
    // the real leaf paths (e.g. `formulaComponents.103/201.levels`) where
    // the section inputs and accordion headers can surface them; a
    // synthetic error on the parent object would never reach any rendered
    // control. Keys outside the expansion (preserved config for branches
    // that failed to resolve) are round-tripped, not edited, so they are
    // deliberately not validated — an error there would be both invisible
    // and unfixable.
    formulaComponents: mixed().when("fieldType", {
      is: (fieldType: FieldDataType) =>
        raw?.referenceKind === "FORMULA" && isNumericFieldType(fieldType),
      then: () =>
        object().shape(
          Object.fromEntries(
            componentPathKeys.map((pathKey) => [pathKey, formulaComponentSettingsSchema]),
          ),
        ),
      otherwise: () => mixed().notRequired(),
    }),
  });

/** Reads the current fieldType from Formik and passes it to DataFormatSection. */
const FieldTypeAwareDataFormat = ({
  queryFormatValues,
  isReadOnly,
}: {
  queryFormatValues?: DataFormatValues;
  isReadOnly: boolean;
}) => {
  const [{ value: fieldType }] = useField<FieldDataType>("fieldType");
  return (
    <DataFormatSection
      title="Data format"
      toggleLabel="Override defaults"
      toggleName="overrideFormat"
      valuesPath="format"
      variant="field"
      inheritedValues={queryFormatValues}
      fieldType={fieldType}
      isReadOnly={isReadOnly}
    />
  );
};

/** Numeric non-formula field types show a single Date period card. */
const DatePeriodAwareSection = ({
  isReadOnly,
  referenceKind,
}: {
  isReadOnly: boolean;
  referenceKind: QueryFieldKind;
}) => {
  const [{ value: fieldType }] = useField<FieldDataType>("fieldType");
  const [{ value: datePeriod }] = useField<FieldSettingsValues["datePeriod"]>("datePeriod");
  const { setFieldValue } = useFormikContext<FieldSettingsValues>();

  useEffect(() => {
    if (referenceKind === "FORMULA") {
      if (datePeriod !== null) {
        void setFieldValue("datePeriod", null, false);
      }
      return;
    }
    if (isNumericFieldType(fieldType)) {
      if (datePeriod === null) {
        void setFieldValue("datePeriod", DEFAULT_DATE_PERIOD_VALUES, false);
      }
      return;
    }
    if (datePeriod !== null) {
      void setFieldValue("datePeriod", null, false);
    }
  }, [datePeriod, fieldType, referenceKind, setFieldValue]);

  if (referenceKind === "FORMULA") return null;
  if (!isNumericFieldType(fieldType) || datePeriod === null) return null;

  return <DatePeriodSection valuesPath="datePeriod" isReadOnly={isReadOnly} />;
};

const LevelsAwareSection = ({
  raw,
  rollupLevelOptions,
  isReadOnly,
  supportedLevels,
}: {
  raw: QueryFieldRow["raw"];
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>;
  isReadOnly: boolean;
  /** The primary-field column's supported level identifiers (empty for
   * NODE fields and columns without catalogue mapping data). */
  supportedLevels: ReadonlyArray<string>;
}) => {
  const [{ value: fieldType }] = useField<FieldDataType>("fieldType");
  const [{ value: levels }] = useField<FieldSettingsValues["levels"]>("levels");
  const { setFieldValue } = useFormikContext<FieldSettingsValues>();
  const showLevels = shouldShowLevels(raw.referenceKind, fieldType, supportedLevels);
  const fieldLevelOptions = useMemo(
    () => filterLevelOptionsBySupported(rollupLevelOptions, supportedLevels),
    [rollupLevelOptions, supportedLevels],
  );
  // Primary fields offer an explicit "None" (no level; page-level
  // execution) and seed it as the default; NODE fields must roll up, so
  // they seed the first catalogue level as before.
  const allowNone = raw.referenceKind === "PRIMARY_FIELD";
  const defaultLevel = allowNone
    ? NONE_LEVEL_VALUE
    : getDefaultRollupLevelValue(fieldLevelOptions);

  useEffect(() => {
    if (showLevels) {
      if (levels.length === 0) {
        // Seed from the persisted stack first: the form can hold an empty
        // stack for a row with saved levels (the catalogue hadn't loaded
        // when the panel opened, so the card was hidden). Seeding "None"
        // here would wipe the saved levels
        const persisted = normalizeLevelStack(raw.levels?.levels ?? [], rollupLevelOptions);
        const seed =
          persisted.length > 0 ? persisted : defaultLevel.length > 0 ? [defaultLevel] : null;
        if (seed) void setFieldValue("levels", seed, false);
      }
      return;
    }
    if (levels.length > 0) {
      void setFieldValue("levels", [], false);
    }
  }, [defaultLevel, levels.length, raw, rollupLevelOptions, setFieldValue, showLevels]);

  if (!showLevels) return null;
  return (
    <LevelsCard
      rollupLevelOptions={fieldLevelOptions}
      isReadOnly={isReadOnly}
      allowNone={allowNone}
    />
  );
};

/** Node leaves offer only VALUE / QUANTITY and default to VALUE; every other
 * kind keeps its persisted type, falling back to the generic default. Used
 * both to seed leaves the persisted tree never carried and to settle ones it
 * carried without settings (`fieldType: null`). */
const resolveComponentFieldType = (
  fieldType: FieldDataType | null,
  componentKind: FormulaComponentAccordionItem["componentKind"],
): FieldDataType =>
  componentKind === ComponentKind.Node
    ? toNodeFieldType(fieldType)
    : (fieldType ?? DEFAULT_FIELD_SETTINGS_VALUES.fieldType);

const buildDefaultComponentSettings = (
  defaultRollupLevel: string,
  componentKind: FormulaComponentAccordionItem["componentKind"],
): FormulaComponentSettings => ({
  datePeriod: { ...DEFAULT_DATE_PERIOD_VALUES },
  levels: defaultRollupLevel.length > 0 ? [defaultRollupLevel] : [],
  fieldType: resolveComponentFieldType(null, componentKind),
  filterRules: { groupsOperator: "and", groups: [] },
  suppressionRules: { groupsOperator: "and", groups: [] },
});

const COMPONENT_SECTION_KEYS = [
  "datePeriod",
  "levels",
  "filterRules",
  "suppressionRules",
] as const;

type ComponentSectionKey = (typeof COMPONENT_SECTION_KEYS)[number];

const FormulaFieldSettingsSections = ({
  referenceKind,
  accordionItems,
  isLoadingFormulaOptions,
  isExpansionComplete,
  canPruneComponents,
  rollupLevelOptions,
  isReadOnly,
  defaultRollupLevel,
  arenaId,
  attributeLeaves,
}: {
  referenceKind: QueryFieldKind;
  accordionItems: ReadonlyArray<FormulaComponentAccordionItem>;
  isLoadingFormulaOptions: boolean;
  isExpansionComplete: boolean;
  /** True only when the expansion resolved fully and is not refetching —
   * the states where `accordionItems` is the proven current leaf set
   * (possibly empty) and missing paths are removals, not unknowns. */
  canPruneComponents: boolean;
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>;
  isReadOnly: boolean;
  defaultRollupLevel: string;
  arenaId: number | null;
  attributeLeaves?: ReadonlyArray<AttributeLeaf>;
}) => {
  const [{ value: fieldType }] = useField<FieldDataType>("fieldType");
  const [{ value: formulaComponents }] =
    useField<FieldSettingsValues["formulaComponents"]>("formulaComponents");
  const [{ value: formulaComponentsResolved }] = useField<boolean>("formulaComponentsResolved");
  const { setFieldValue, errors, submitCount } = useFormikContext<FieldSettingsValues>();

  const mergedComponents = useMemo(() => {
    // No early return on empty `accordionItems`: a formula can legitimately
    // resolve to zero leaves (all components removed), and the prune below
    // must still clear the stale buffer in that case.
    if (referenceKind !== "FORMULA") {
      return formulaComponents;
    }

    let changed = false;
    const next: Record<string, FormulaComponentSettings> = { ...formulaComponents };
    const validPathKeys = new Set(accordionItems.map((item) => item.pathKey));

    for (const item of accordionItems) {
      if (!next[item.pathKey]) {
        next[item.pathKey] = buildDefaultComponentSettings(
          defaultRollupLevel,
          item.componentKind,
        );
        changed = true;
        continue;
      }
      const resolvedFieldType = resolveComponentFieldType(
        next[item.pathKey].fieldType,
        item.componentKind,
      );
      if (resolvedFieldType !== next[item.pathKey].fieldType) {
        next[item.pathKey] = { ...next[item.pathKey], fieldType: resolvedFieldType };
        changed = true;
      }
      if (next[item.pathKey].levels.length === 0 && defaultRollupLevel.length > 0) {
        next[item.pathKey] = { ...next[item.pathKey], levels: [defaultRollupLevel] };
        changed = true;
      }
    }

    // Prune paths no longer in the expansion (edited-out / archived
    // components); their stale config drops on the next save. Prune only
    // on proof of removal: while the expansion is incomplete or still
    // fetching, a missing anchor is an unknown, not a removal, so its
    // saved config must round-trip untouched.
    if (canPruneComponents) {
      for (const key of Object.keys(next)) {
        if (!validPathKeys.has(key)) {
          delete next[key];
          changed = true;
        }
      }
    }

    return changed ? next : formulaComponents;
  }, [
    accordionItems,
    canPruneComponents,
    defaultRollupLevel,
    formulaComponents,
    referenceKind,
  ]);

  useEffect(() => {
    if (mergedComponents !== formulaComponents) {
      void setFieldValue("formulaComponents", mergedComponents, false);
    }
  }, [formulaComponents, mergedComponents, setFieldValue]);

  // Latch the resolved flag once the buffer provably mirrors the current
  // leaf set, so the save mapper treats an empty buffer as "formula has no
  // leaves — clear persisted components" instead of "never loaded".
  useEffect(() => {
    if (canPruneComponents && !formulaComponentsResolved) {
      void setFieldValue("formulaComponentsResolved", true, false);
    }
  }, [canPruneComponents, formulaComponentsResolved, setFieldValue]);

  // Per-section sets of pathKeys whose settings failed validation on the
  // last submit; drives the error pill + auto-expand on the accordion
  // cards, since a collapsed accordion unmounts the inputs that would
  // otherwise show the inline error.
  const erroredPathKeysBySection = useMemo(() => {
    const bySection: Record<ComponentSectionKey, Set<string>> = {
      datePeriod: new Set(),
      levels: new Set(),
      filterRules: new Set(),
      suppressionRules: new Set(),
    };
    if (submitCount === 0) return bySection;
    for (const item of accordionItems) {
      for (const section of COMPONENT_SECTION_KEYS) {
        if (getIn(errors, `formulaComponents.${item.pathKey}.${section}`)) {
          bySection[section].add(item.pathKey);
        }
      }
    }
    return bySection;
  }, [accordionItems, errors, submitCount]);

  const componentsReady =
    accordionItems.length > 0 &&
    accordionItems.every((item) => formulaComponents[item.pathKey] != null);

  if (referenceKind !== "FORMULA" || !isNumericFieldType(fieldType)) return null;

  const showLoading =
    isLoadingFormulaOptions || (accordionItems.length > 0 && !componentsReady);
  const items = showLoading ? [] : accordionItems;
  const nodeItems = items.filter((item) => item.componentKind === ComponentKind.Node);

  // Same layout as the non-formula sections: one card per setting, each
  // holding one accordion per component occurrence of the expanded
  // formula tree.
  return (
    <>
      {!showLoading && !isExpansionComplete && (
        <Banner
          variant="warning"
          title="Some formula components couldn't be loaded"
          description="Their saved settings are kept unchanged."
        />
      )}
      <FormulaComponentAccordionCard
        cardTitle="Node types"
        items={nodeItems}
        isLoading={showLoading}
        erroredPathKeys={new Set()}
        submitCount={submitCount}
        renderPanel={(pathKey) => (
          <NodeComponentFieldTypeRadio
            name={`formulaComponents.${pathKey}.fieldType`}
            isReadOnly={isReadOnly}
          />
        )}
      />
      <FormulaComponentAccordionCard
        cardTitle="Date period"
        items={items}
        isLoading={showLoading}
        erroredPathKeys={erroredPathKeysBySection.datePeriod}
        submitCount={submitCount}
        renderPanel={(pathKey) => (
          <DatePeriodSection
            valuesPath={`formulaComponents.${pathKey}.datePeriod`}
            variant="inline"
            isReadOnly={isReadOnly}
          />
        )}
      />
      <FormulaComponentAccordionCard
        cardTitle="Levels"
        items={items}
        isLoading={showLoading}
        erroredPathKeys={erroredPathKeysBySection.levels}
        submitCount={submitCount}
        renderPanel={(pathKey) => (
          <LevelsCard
            valuesPath={`formulaComponents.${pathKey}.levels`}
            variant="inline"
            rollupLevelOptions={rollupLevelOptions}
            isReadOnly={isReadOnly}
          />
        )}
      />
      <FormulaComponentAccordionCard
        cardTitle="Filter rules"
        items={items}
        isLoading={showLoading}
        erroredPathKeys={erroredPathKeysBySection.filterRules}
        submitCount={submitCount}
        renderPanel={(pathKey) => (
          <RulesSection
            title="Filter rules"
            description="What data would you like to include to calculate this component"
            name={`formulaComponents.${pathKey}.filterRules`}
            variant="inline"
            isReadOnly={isReadOnly}
            arenaId={arenaId}
            allowRuntimeValues
            attributeLeaves={attributeLeaves}
          />
        )}
      />
      <FormulaComponentAccordionCard
        cardTitle="Suppression rules"
        items={items}
        isLoading={showLoading}
        erroredPathKeys={erroredPathKeysBySection.suppressionRules}
        submitCount={submitCount}
        renderPanel={(pathKey) => (
          <RulesSection
            title="Suppression rules"
            description="When do you want to hide this component"
            name={`formulaComponents.${pathKey}.suppressionRules`}
            variant="inline"
            isReadOnly={isReadOnly}
            arenaId={arenaId}
            allowRuntimeValues
            attributeLeaves={attributeLeaves}
          />
        )}
      />
    </>
  );
};

export const FieldSettingsPanel = ({
  isOpen,
  onClose,
  fieldRow,
  onSave,
  queryFormatValues,
  isReadOnly = false,
  arenaId = null,
  rollupLevelOptions,
  attributeLeaves,
  typeHierarchy = null,
  formulas = [],
  pendingFormulaComponents,
}: FieldSettingsPanelProps) => {
  // Hold the most-recent non-null row so the drawer can animate closed
  // showing the same contents the user just clicked Save on. Without this,
  // the parent clears `fieldRow` immediately on save/close and the drawer
  // would slide out empty. `onCloseAnimationComplete` clears the cache so
  // a reopen of the same field re-mounts the Form (otherwise stale unsaved
  // edits would persist across close-without-save).
  const [displayRow, setDisplayRow] = useState<QueryFieldRow | null>(fieldRow);
  // Derive during render (React docs pattern): syncs the cache to the
  // latest non-null row without an effect, avoiding the cascading-render
  // lint and keeping the form's `initialValues` memo seeded on the same
  // commit the parent flips `isOpen` to true.
  if (fieldRow && fieldRow !== displayRow) {
    setDisplayRow(fieldRow);
  }

  const handleCloseAnimationComplete = useCallback(() => {
    setDisplayRow(null);
  }, []);

  const defaultRollupLevel = useMemo(
    () => getDefaultRollupLevelValue(rollupLevelOptions),
    [rollupLevelOptions],
  );

  const displayRaw = displayRow?.raw ?? null;

  const {
    accordionItems,
    isLoading: isLoadingFormulaOptions,
    isExpansionComplete,
    hasResolvedExpansion,
  } = useFormulaComponentItems({
    arenaId,
    formulaId: displayRaw?.formulaId,
    typeHierarchy,
    formulas,
    attributeLeaves: attributeLeaves ?? [],
    enabled: displayRaw?.referenceKind === "FORMULA",
  });

  const isResolvingFormulaOptions = isLoadingFormulaOptions && accordionItems.length === 0;

  // `isExpansionComplete` alone is not proof the leaf set loaded: it stays
  // true before the first fetch resolves (and while disabled). Only a
  // resolved, complete, non-fetching expansion makes `accordionItems` the
  // authoritative leaf set — including the zero-leaf case.
  const canPruneComponents =
    hasResolvedExpansion && isExpansionComplete && !isLoadingFormulaOptions;

  // The primary-field column's supported levels drive whether the Levels
  // card shows for non-numeric fields and which options it offers.
  const supportedLevels = useMemo(
    () => (displayRaw ? toPrimaryFieldSupportedLevels(displayRaw, attributeLeaves) : []),
    [displayRaw, attributeLeaves],
  );

  // Stable reference: `enableReinitialize` only resets when `raw` changes
  // (i.e. the user opens a different field), not on every keystroke.
  // For NODE fields with no saved levels, default to the first catalogue
  // entry so the card always shows at least one row.
  const initialValues = useMemo(() => {
    if (!displayRaw) return null;
    const values = toFieldSettingsValues(
      displayRaw,
      queryFormatValues,
      attributeLeaves,
      rollupLevelOptions,
    );
    let next = values;
    const fieldDefaultLevel = getDefaultRollupLevelValue(
      filterLevelOptionsBySupported(rollupLevelOptions, supportedLevels),
    );
    const seedDefaultLevel =
      next.levels.length === 0 &&
      fieldDefaultLevel.length > 0 &&
      shouldShowLevels(displayRaw.referenceKind, next.fieldType, supportedLevels);
    if (seedDefaultLevel) {
      next = { ...next, levels: [fieldDefaultLevel] };
    }
    // A buffered-but-empty map means the previous panel session never
    // loaded the expansion — fall back to the persisted components rather
    // than overriding them with nothing.
    const hasPendingComponents =
      pendingFormulaComponents != null && Object.keys(pendingFormulaComponents).length > 0;
    next = {
      ...next,
      formulaComponents: hasPendingComponents
        ? pendingFormulaComponents
        : next.formulaComponents,
    };
    return next;
  }, [
    displayRaw,
    pendingFormulaComponents,
    queryFormatValues,
    attributeLeaves,
    rollupLevelOptions,
    supportedLevels,
  ]);

  const validationSchema = useMemo(
    () =>
      buildValidationSchema(
        displayRaw,
        accordionItems.map((item) => item.pathKey),
        supportedLevels,
      ),
    [accordionItems, displayRaw, supportedLevels],
  );

  if (!displayRow || !initialValues) {
    return (
      <SlideoutDrawerV2
        isOpen={isOpen}
        onClose={onClose}
        isDismissable
        overlayStyle={OverlayStyle.Transparent}
        zIndex={Z_INDEX.drawer}
        data-testid="field-settings-panel"
      />
    );
  }

  const { display: field, raw } = displayRow;

  return (
    <SlideoutDrawerV2
      isOpen={isOpen}
      onClose={onClose}
      onCloseAnimationComplete={handleCloseAnimationComplete}
      isDismissable={false}
      overlayStyle={OverlayStyle.Transparent}
      zIndex={Z_INDEX.drawer}
      data-testid="field-settings-panel"
    >
      <div className={styles.drawerWidth} data-testid="field-settings-form">
        <Form<FieldSettingsValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => onSave(field.id, values)}
          submitOnlyChangedValues={false}
          onSubmitErrorMessage="Something went wrong saving these settings"
          enableReinitialize
        >
          <Flex flexDirection="column" className={styles.form}>
            <SlideoutDrawerV2.ComplexHeader
              className={styles.header}
              title={getNameDisplayText(field)}
              subtitles={[
                <Text key="merge" variant="secondary-md" asSpan>
                  {toTemplateTag(field.mergeField, field.isTransactionLevel)}
                </Text>,
              ]}
              closeButton={
                <Button
                  variant="custom"
                  textColor={Colors.Neutral700}
                  backgroundColor={Colors.Neutral0}
                  icon="xmark"
                  iconPosition="right"
                  data-testid="field-settings-close-btn"
                >
                  {isReadOnly ? "Close" : "Close without saving"}
                </Button>
              }
              showGap={false}
              showBorder={false}
            />
            <Flex flexDirection="column" gap={SPACING.LARGE} className={styles.body}>
              <GeneralCard
                referenceKind={raw.referenceKind}
                isReadOnly={isReadOnly}
                queryDecimalPlaces={queryFormatValues?.decimalPlaces}
              />
              <DatePeriodAwareSection
                isReadOnly={isReadOnly}
                referenceKind={raw.referenceKind}
              />
              <FormulaFieldSettingsSections
                referenceKind={raw.referenceKind}
                accordionItems={accordionItems}
                isLoadingFormulaOptions={isResolvingFormulaOptions}
                isExpansionComplete={isExpansionComplete}
                canPruneComponents={canPruneComponents}
                rollupLevelOptions={rollupLevelOptions}
                isReadOnly={isReadOnly}
                defaultRollupLevel={defaultRollupLevel}
                arenaId={arenaId}
                attributeLeaves={attributeLeaves}
              />
              <LevelsAwareSection
                raw={raw}
                rollupLevelOptions={rollupLevelOptions}
                isReadOnly={isReadOnly}
                supportedLevels={supportedLevels}
              />
              {/* FORMULA fields configure rules per component (accordion
                  cards above); showing the field-level pair as well would
                  stack two identically-titled rules cards. */}
              {raw.referenceKind !== "DYNAMIC_DATE" && raw.referenceKind !== "FORMULA" && (
                <>
                  <RulesSection
                    title="Filter rules"
                    description="What data would you like to include to calculate this field"
                    name="filterRules"
                    isReadOnly={isReadOnly}
                    arenaId={arenaId}
                    allowRuntimeValues
                    attributeLeaves={attributeLeaves}
                  />
                  <RulesSection
                    title="Suppression rules"
                    description="When do you want to hide this field"
                    name="suppressionRules"
                    isReadOnly={isReadOnly}
                    arenaId={arenaId}
                    allowRuntimeValues
                    attributeLeaves={attributeLeaves}
                  />
                </>
              )}
              <FieldTypeAwareDataFormat
                queryFormatValues={queryFormatValues}
                isReadOnly={isReadOnly}
              />
            </Flex>
            {!isReadOnly && (
              <Flex alignItems="center" justifyContent="flex-end" className={styles.footer}>
                <SubmitButton data-testid="save-field-settings-btn">Save settings</SubmitButton>
              </Flex>
            )}
          </Flex>
        </Form>
      </div>
    </SlideoutDrawerV2>
  );
};
